import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * Shared top-bar chrome for both the Dragon HQ launcher and every installed
 * app's root UI. Enforces one header height, one background, one logo size,
 * one gap rhythm, and one brand-left / pill / center / right slot pattern.
 *
 * Responsive behaviour is baked into the shell's Tailwind classes so callers
 * don't repeat it. A caller still decides WHICH nodes go into which slot
 * (e.g. Workspace passes a back-chevron + contextual title as `leadingAction`
 * only when drilled-in on mobile).
 *
 * `leadingAction` is mobile-only and, when present, replaces the brand on
 * small screens — matching the old Workspace behaviour of collapsing the
 * brand to reclaim horizontal space during drill-down. The brand still shows
 * at md+ alongside (well, instead of — leadingAction is md:hidden) so desktop
 * always anchors on the Dragon HQ wordmark.
 */
export interface AppContext {
  icon: LucideIcon;
  name: string;
  /**
   * Back-to-launcher handler. Omit in single-app builds so the logo renders
   * as plain branding instead of a button that bounces through an
   * auto-redirecting launcher.
   */
  onExit?: () => void;
}

export interface PortalHeaderProps {
  leadingAction?: ReactNode;
  appContext?: AppContext;
  /** Expanded-width middle slot (e.g. Workspace's search-bar button).
   *  Desktop only — hidden below lg. */
  center?: ReactNode;
  /** Desktop action strip (StatusLight + buttons). Hidden below lg; callers
   *  should pass `overflow` for the mobile/tablet equivalent. */
  right?: ReactNode;
  /** Mobile/tablet overflow cluster (StatusLight + MoreVertical dropdown).
   *  Hidden at lg+. */
  overflow?: ReactNode;
}

export function PortalHeader({
  leadingAction,
  appContext,
  center,
  right,
  overflow,
}: PortalHeaderProps) {
  const AppIcon = appContext?.icon;
  return (
    <header className="h-12 flex items-center gap-2 lg:gap-3 border-b border-ink-800 bg-ink-900 px-3 lg:px-4">
      {leadingAction && (
        <div className="md:hidden flex items-center gap-2 min-w-0 flex-1">
          {leadingAction}
        </div>
      )}

      {/* Brand: logo only. Clickable back-to-launcher when inside an app,
          plain image on the launcher itself. Hidden on mobile when
          leadingAction is taking over the left cluster (drilled-in state). */}
      <div
        className={
          'items-center flex-shrink-0 ' +
          (leadingAction ? 'hidden md:flex' : 'flex')
        }
      >
        {appContext?.onExit ? (
          <button
            type="button"
            onClick={appContext.onExit}
            title="Back to launcher"
            className="flex items-center rounded-md p-1 -m-1 hover:bg-ink-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dragon-500"
          >
            <img src="/dragon-logo.png" alt="Dragon — back to launcher" className="h-6 w-auto" />
          </button>
        ) : (
          <img src="/dragon-logo.png" alt="Dragon" className="h-6 w-auto" />
        )}
      </div>

      {/* Divider + app pill. Hidden on mobile when leadingAction is taking
          over the left cluster (drilled-in state). */}
      {appContext && AppIcon && (
        <div
          className={
            'items-center gap-2 ' +
            (leadingAction ? 'hidden md:flex' : 'flex')
          }
        >
          <span className="text-ink-700 select-none" aria-hidden>
            |
          </span>
          <div className="flex items-center gap-1.5 px-2 py-1 text-sm text-ink-100">
            <AppIcon size={16} className="text-dragon-500 flex-shrink-0" />
            <span className="hidden sm:inline">{appContext.name}</span>
          </div>
        </div>
      )}

      {/* Desktop center slot */}
      {center && <div className="hidden lg:flex flex-1 min-w-0">{center}</div>}

      {/* Spacer when no center. Skipped when leadingAction is consuming
          flex-1 on mobile (that branch above already takes the width). */}
      {!center && !leadingAction && <div className="flex-1" />}
      {!center && leadingAction && <div className="hidden md:block flex-1" />}

      {/* Desktop right cluster */}
      {right && <div className="hidden lg:flex items-center gap-2">{right}</div>}

      {/* Mobile + tablet overflow cluster */}
      {overflow && <div className="flex lg:hidden items-center gap-2">{overflow}</div>}
    </header>
  );
}
